<template>
  <div :class="$x.cvvForm">
    <div :class="$x.cvvFormWrap">
      <VForm ref="cvvForm" name="cvvForm" class="cvvFormElement">
        <VTextField
          v-model="cvv"
          name="cvv"
          type="tel"
          :maxlength="maxLength"
          validate-on-blur
          :class="[$x.cvvInput]"
          :label="t.cvvLabel"
          :rules="rules"
          @input="cvvChanged"
        ></VTextField>
        <div @click="showCreditCardImage($event)">
          <NsIcon id="cc-tooltip" icon-name="icon-info"></NsIcon>
          <div id="creditCardImage" class="creditCardImage">
            <img
              v-show="amex"
              :class="tooltipClass"
              src="https://test.nuskin.com/content/dam/global/images/amex-examp.gif"
            />
            <img
              v-show="!amex"
              :class="tooltipClass"
              src="https://test.nuskin.com/content/dam/global/images/ccvalexamp.gif"
            />
          </div>
        </div>
      </VForm>
      <button
        v-if="!buttonless"
        class="hit-solid"
        :class="$x.formContinue"
        name="selectCvv"
        @click="doContinue"
      >
        {{ t.continue }}
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Original Author: Treg Anderson
 * Original Team: Mavericks
 */
import { VForm, VTextField } from 'vuetify/lib';
import { NsIcon } from '@nuskin/ns-icon';

export default {
  name: 'NsCvvForm',
  components: {
    NsIcon,
    VForm,
    VTextField,
  },
  props: {
    value: {
      type: String,
      default() {
        return '';
      },
    },

    buttonless: {
      type: Boolean,
      default() {
        return false;
      },
    },

    amex: {
      type: Boolean,
      default() {
        return false;
      },
    },

    translations: {
      type: Object,
      default() {
        return {
          continue: 'Continue',
          cvvLabel: 'CVV',
          fieldRequired: 'This field is required',
          cvvInvalid: 'CVV must be at least three digits',
        };
      },
    },
  },
  data() {
    return {
      cvv: this.value,
      rules: [],
      t: this.translations,
      maxLength: this.amex ? 4 : 3,
      tooltipClass: 'card-image-tooltip-left',
    };
  },
  watch: {
    translations() {
      this.t = this.translations;
      if (Object.keys(this.t).length > 0) {
        this.initRules();
      }
    },
    amex() {
      this.maxLength = this.amex ? 4 : 3;
    },
    value() {},
  },
  mounted() {
    this.initRules();
    document.addEventListener('click', () => {
      const cc = document.getElementById('creditCardImage');
      if (cc && cc.style.display === 'block') {
        cc.style.display = 'none';
      }
    });
    this.setCardImagePosition();
  },
  methods: {
    doContinue() {
      if (this.$refs.cvvForm.validate()) {
        this.$emit('input', this.cvv);
      }
    },

    cvvChanged(value) {
      if (this.buttonless) {
        const isValid = this.rules.reduce((valid, rule) => {
          return valid && typeof rule(value) !== 'string';
        }, true);
        this.$emit('input', isValid ? this.cvv : '');
      }
    },
    setCardImagePosition() {
      const rect = document.body.getBoundingClientRect();
      const elRect = document
        .getElementById('cc-tooltip')
        .getBoundingClientRect();
      if (elRect.left - rect.left > (rect.right - rect.left) / 2) {
        this.tooltipClass = 'card-image-tooltip-left';
      } else {
        this.tooltipClass = 'card-image-tooltip-right';
      }
    },
    showCreditCardImage(event) {
      event.stopPropagation();
      if (/Mobi|Android/i.test(navigator.userAgent)) {
        try {
          const cc = document.getElementById('creditCardImage');
          cc.style.display = cc.style.display === 'block' ? 'none' : 'block';
        } catch (err) {
          // console.log(`Failed to show credit card image ${err}`);
        }
      }
    },

    initRules() {
      this.rules = [
        (value) => !!value || this.t.fieldRequired || 'This field is required',
        (value) =>
          /^\d{3,4}$/.test(value) ||
          this.t.cvvInvalid ||
          'CVV must be at least three digits',
      ];
    },
  },
};
</script>

<style lang="scss" module="$x">
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";

.cvvForm {
  position: relative;
  .formContinue {
    width: 100px;
    height: 34px;
    margin-top: 4px;
    padding: 0 20px 0 20px;
    @media screen and (max-width: 767px) {
      margin-top: 4px;
    }
  }
}

.cvvFormWrap {
  display: flex;
  flex-wrap: wrap;
}

.cvvInput {
  input {
    width: 80px;
  }
}
</style>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

*:not(.v-icon) {
  font-family: map.get(typographyVariables.$typography, 'font-family-primary');
}
</style>

<style lang="scss">
[class^='NsCvvForm__'] {
  .v-form {
    margin-right: 10px !important;
  }

  .v-input__slot {
    &::before {
      border-width: 0 !important;
      transition: none !important;
    }
  }

  .v-text-field__slot {
    align-items: center;

    label {
      margin-bottom: 0;
      padding-right: 15px;
      overflow: visible;
    }

    input {
      max-width: 80px;
    }
  }

  .NsCvvForm__cvvFormWrap {
    .cvvFormElement {
      display: flex;
      .creditCardImage {
        display: none;
        position: relative;
        height: 0px;
        width: 0px;
        .card-image-tooltip-right {
          position: absolute;
          top: -125px;
          left: 25px;
          z-index: 100;
        }
        .card-image-tooltip-left {
          position: absolute;
          top: -125px;
          left: -255px;
          z-index: 100;
        }
      }
      .icon {
        pointer-events: auto !important;
        margin-left: 5px;
        height: 40px !important;
      }

      .icon:hover + .creditCardImage {
        display: inline-block;
      }
    }
  }
}
</style>
